import type { ComponentType } from "react";
import {
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
} from "lucide-react";
import {
  DEFAULT_LEGEND_POSITION,
  LEGEND_POSITIONS,
} from "../../services/chartStudio/legendPosition";
import { rawHex } from "../../design-system/tokens/colors";
import {
  chartCurrencyFormatOptions,
  chartPercentageFormatOptions,
} from "../../services/chartStudio/chartNumberFormats";
import type { ChartNumberFormatOption } from "../../services/chartStudio/chartNumberFormats";

/**
 * Chart Studio → Format tab, as pure data.
 * Figma: `z2UTps4F1dKNh1bbD4LtYI` node `6409:556187` ("Expanded").
 *
 * Same contract as `menus.ts`: structure and copy live here, handlers and state
 * live at the usage site keyed by `key` / `id`. Never JSX, never callbacks.
 * Adding a section is an entry in `chartFormatSections` — no panel JSX changes.
 */

/** Boolean settings — one Switch row each. */
export type ChartFormatToggleKey =
  | "showChartTitle"
  | "showLabels"
  | "showXAxis"
  | "showXAxisTitle"
  | "showYAxis"
  | "showYAxisTitle"
  | "showLegends"
  | "showXAxisGridlines"
  | "showYAxisGridlines"
  | "showDirectValues"
  | "showTooltip";

/** Numeric settings — one range slider each. */
export type ChartFormatSliderKey = "xAxisLabelAngle" | "yAxisLabelAngle";

/** Single-choice settings — one segmented picker group each. */
export type ChartFormatSegmentedKey = "legendPosition";

/** Free-text settings — one editable input each. */
export type ChartFormatTextKey = "chartTitle" | "xAxisTitle" | "yAxisTitle";

/**
 * Settings chosen from a menu — Figma draws a chevron on exactly these three
 * (6409:556196, 6409:556393, 6409:556404) and hides it on the text fields.
 */
export type ChartFormatSelectKey = "colorPalette" | "currency" | "percentage";

/**
 * The panel's value contract, derived from the key unions above so config and
 * state cannot drift — adding a toggle here forces a default to be supplied.
 */
export type ChartFormatValues = Record<ChartFormatToggleKey, boolean> &
  Record<ChartFormatSliderKey, number> &
  Record<ChartFormatSegmentedKey, string> &
  Record<ChartFormatTextKey, string> &
  Record<ChartFormatSelectKey, string>;

/**
 * Where a row's value lives in the v2 item `format` (a dot path into
 * `FormatApi`). A row without one has no backing field in the API yet and
 * renders disabled, showing what the chart's spec draws.
 */
export type ChartFormatApiPath =
  | "color_palette"
  | "title.show"
  | "title.text"
  | "labels.show"
  | "data_values.show"
  | "legend.show"
  | "tooltip.show"
  | "axes.x.show_gridlines"
  | "axes.y.show_gridlines"
  | "axes.x.show"
  | "axes.x.show_title"
  | "axes.x.title"
  | "axes.x.label_angle"
  | "axes.y.show"
  | "axes.y.show_title"
  | "axes.y.title"
  | "axes.y.label_angle";

export interface ChartFormatToggleRow {
  kind: "toggle";
  key: ChartFormatToggleKey;
  label: string;
  /** Second line under the label — Figma has it on two rows only. */
  description?: string;
  apiPath?: ChartFormatApiPath;
}

export interface ChartFormatFieldRow {
  kind: "field";
  /**
   * Which `ChartFormatValues` entry this row reads and writes. A `select` key
   * holds the chosen option's label; a text key holds what the user typed.
   */
  key: ChartFormatTextKey | ChartFormatSelectKey;
  label: string;
  /** Chevron + button semantics. Figma draws one only where a menu opens. */
  select?: boolean;
  /** Leading 16px colour dot — the Colors palette row only. */
  swatchColor?: string;
  /** Shown while the text field is empty. Ignored by `select` rows. */
  placeholder?: string;
  /**
   * The menu a `select` row opens. Supplied for the number-format rows only —
   * `colorPalette` opens `ChartColorPaletteMenu` instead, which is a palette
   * editor rather than a list of values.
   */
  options?: ChartNumberFormatOption[];
  apiPath?: ChartFormatApiPath;
}

export interface ChartFormatSliderRow {
  kind: "slider";
  key: ChartFormatSliderKey;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
  apiPath?: ChartFormatApiPath;
}

export interface ChartFormatSegmentedOption {
  id: string;
  /** These options are icon-only, so this is their whole accessible name. */
  ariaLabel: string;
  /** Component reference, not JSX, so the config stays pure data. */
  icon: ComponentType<{ size?: number | string }>;
  dividerBefore?: boolean;
}

export interface ChartFormatSegmentedGroup {
  key: ChartFormatSegmentedKey;
  ariaLabel: string;
  options: ChartFormatSegmentedOption[];
}

export interface ChartFormatSegmentedRow {
  kind: "segmented";
  label: string;
  /** Toggle key that must be enabled before this dependent control is useful. */
  visibleWhen?: ChartFormatToggleKey;
  /** Rendered left→right, sharing the row evenly. */
  groups: ChartFormatSegmentedGroup[];
}

export type ChartFormatRow =
  | ChartFormatToggleRow
  | ChartFormatFieldRow
  | ChartFormatSliderRow
  | ChartFormatSegmentedRow;

export interface ChartFormatSection {
  id: string;
  /** Caption above the card. */
  title: string;
  rows: ChartFormatRow[];
}

/** Palette seeds shown by the Format → Color palette menu (Figma 6433:563110). */
export interface ChartColorPaletteDefinition {
  id: string;
  label: string;
  colors: string[];
  source: "builtin";
  /**
   * What `format.color_palette.value` carries for this built-in
   * (`{ kind: "built_in", value }`): the server's `BUILT_IN_SCHEMES` keys.
   */
  apiValue: string;
  /** Values older builds wrote for this built-in; read as it, never sent. */
  legacyApiValues?: string[];
}

export const chartColorPaletteDefinitions: ChartColorPaletteDefinition[] = [
  {
    id: "magicbi",
    apiValue: "magicbi_palette",
    label: "MagicBI palette",
    colors: [
      rawHex.primary[500], rawHex.primary[400], rawHex.primary[300],
      rawHex.primary[200], rawHex.primary[100],
    ],
    source: "builtin",
  },
  {
    id: "most-trending",
    apiValue: "most_trending",
    legacyApiValues: ["most_trending_palette"],
    label: "Most Trending",
    colors: [
      "#FFC28B", "#FF9463", "#FE6D4E", "#FF5342",
      "#819898", "#4C7D8E", "#174D7C", "#1C284E",
    ],
    source: "builtin",
  },
  {
    id: "tableau",
    apiValue: "tableau",
    legacyApiValues: ["tableau_palette"],
    label: "Tableau",
    colors: [
      "#4C78A8", "#F58518", "#E45756", "#72B7B2", "#54A24B",
      "#EECA3B", "#B279A2", "#FF9DA6", "#9D755D", "#BAB0AC",
    ],
    source: "builtin",
  },
];

/** Both axis-angle sliders — Figma 6409:556277 and 6409:556321. */
const AXIS_ANGLE = { min: -90, max: 90, step: 15, unit: "°" };

/**
 * The four legend positions, in `LEGEND_POSITIONS` order — one button per Vega
 * edge orient, and the option id IS the orient (see `legendPosition.ts`).
 *
 * Glyphs are the `lucide-react` icons Figma exported for the old two-group
 * control, kept for the meaning each already carried: an `Align*Vertical` icon
 * draws a vertical guide line, so it reads as left/right, and `Align*Horizontal`
 * reads as top/bottom. No new icons, no new atoms.
 */
const LEGEND_ICONS: Record<
  (typeof LEGEND_POSITIONS)[number],
  ChartFormatSegmentedOption["icon"]
> = {
  left: AlignStartVertical,
  right: AlignEndVertical,
  top: AlignStartHorizontal,
  bottom: AlignEndHorizontal,
};

const LEGEND_POSITION_OPTIONS: ChartFormatSegmentedOption[] = LEGEND_POSITIONS.map(
  (position, index) => ({
    id: position,
    ariaLabel: `Legend ${position}`,
    icon: LEGEND_ICONS[position],
    ...(index > 0 ? { dividerBefore: true as const } : {}),
  }),
);

/** The ten sections in Figma's vertical order (y 0 → 1413). */
export const chartFormatSections: ChartFormatSection[] = [
  {
    id: "colors",
    title: "Colors",
    rows: [
      {
        kind: "field",
        key: "colorPalette",
        label: "Color palette",
        select: true,
        apiPath: "color_palette",
        swatchColor: rawHex.primary[500],
      },
    ],
  },
  {
    id: "chartTitle",
    title: "Chart title",
    rows: [
      { kind: "toggle", key: "showChartTitle", label: "Show chart title", apiPath: "title.show" },
      {
        kind: "field",
        key: "chartTitle",
        label: "Chart title",
        placeholder: "Name this chart",
        apiPath: "title.text",
      },
    ],
  },
  {
    id: "labels",
    title: "Labels",
    rows: [
      {
        kind: "toggle",
        key: "showLabels",
        label: "Show labels",
        description: "Category names on the chart",
        apiPath: "labels.show",
      },
    ],
  },
  {
    id: "xAxis",
    title: "X-axis",
    rows: [
      { kind: "toggle", key: "showXAxis", label: "Show X-axis", apiPath: "axes.x.show" },
      {
        kind: "toggle",
        key: "showXAxisTitle",
        label: "Show axis title",
        apiPath: "axes.x.show_title",
      },
      {
        kind: "field",
        key: "xAxisTitle",
        label: "Axis title",
        placeholder: "Name this axis",
        apiPath: "axes.x.title",
      },
      {
        kind: "slider",
        key: "xAxisLabelAngle",
        label: "X-axis label angles",
        ...AXIS_ANGLE,
        apiPath: "axes.x.label_angle",
      },
    ],
  },
  {
    id: "yAxis",
    title: "Y-axis",
    rows: [
      { kind: "toggle", key: "showYAxis", label: "Show Y-axis", apiPath: "axes.y.show" },
      {
        kind: "toggle",
        key: "showYAxisTitle",
        label: "Show axis title",
        apiPath: "axes.y.show_title",
      },
      {
        kind: "field",
        key: "yAxisTitle",
        label: "Axis title",
        placeholder: "Name this axis",
        apiPath: "axes.y.title",
      },
      {
        kind: "slider",
        key: "yAxisLabelAngle",
        label: "Y-axis label angles",
        ...AXIS_ANGLE,
        apiPath: "axes.y.label_angle",
      },
    ],
  },
  {
    id: "legends",
    title: "Legends",
    rows: [
      { kind: "toggle", key: "showLegends", label: "Show legends", apiPath: "legend.show" },
      {
        kind: "segmented",
        label: "Legend position",
        visibleWhen: "showLegends",
        groups: [
          {
            key: "legendPosition",
            ariaLabel: "Legend position",
            options: LEGEND_POSITION_OPTIONS,
          },
        ],
      },
    ],
  },
  {
    id: "gridlines",
    title: "Gridlines",
    rows: [
      {
        kind: "toggle",
        key: "showXAxisGridlines",
        label: "Show X-axis gridlines",
        apiPath: "axes.x.show_gridlines",
      },
      {
        kind: "toggle",
        key: "showYAxisGridlines",
        label: "Show Y-axis gridlines",
        apiPath: "axes.y.show_gridlines",
      },
    ],
  },
  {
    id: "dataValues",
    title: "Data values",
    rows: [
      {
        kind: "toggle",
        key: "showDirectValues",
        label: "Show direct values",
        description: "Numeric values on each data point",
        // `format.labels` is category names (the row above); numeric values
        // on the marks are their own `data_values` section.
        apiPath: "data_values.show",
      },
    ],
  },
  {
    id: "numberFormatting",
    title: "Number formatting",
    rows: [
      {
        kind: "field",
        key: "currency",
        label: "Currency",
        select: true,
        options: chartCurrencyFormatOptions,
      },
      {
        kind: "field",
        key: "percentage",
        label: "Percentage",
        select: true,
        options: chartPercentageFormatOptions,
      },
    ],
  },
  {
    id: "tooltip",
    title: "Tooltip",
    rows: [
      { kind: "toggle", key: "showTooltip", label: "Show tooltip on hover", apiPath: "tooltip.show" },
    ],
  },
];

/**
 * The panel's opening state — the values Figma shows in node 6409:556187.
 *
 * These are seeds, not structure: the user edits the text fields and picks from
 * the select menus, so the strings cannot live on the rows themselves. Typing
 * this as `ChartFormatValues` means a key added to any union above fails to
 * compile until its default is supplied here.
 */
export const chartFormatDefaults: ChartFormatValues = {
  showChartTitle: true,
  showLabels: true,
  showXAxis: true,
  showXAxisTitle: true,
  showYAxis: true,
  showYAxisTitle: true,
  showLegends: false,
  showXAxisGridlines: false,
  showYAxisGridlines: false,
  showDirectValues: true,
  showTooltip: true,
  xAxisLabelAngle: 0,
  yAxisLabelAngle: 0,
  legendPosition: DEFAULT_LEGEND_POSITION,
  chartTitle: "Revenue performance vs. target",
  xAxisTitle: "Month",
  yAxisTitle: "Sales",
  colorPalette: "MagicBI palette",
  currency: "$5M",
  percentage: "12.5%",
};
